'use client';
import { ArrowUp, Lock, LockOpen } from 'lucide-react';
import { type ForwardedRef, forwardRef, useEffect, useState } from 'react';

import Input from './Input';

interface Props {
  value: string;
  onChange?: (value: string) => void;
  classNames?: string;
  autoFocus?: boolean;
  warning?: boolean;
  icons?: React.ReactNode[];
  label?: string;
  inputClassName?: string;
}

const PasswordInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      warning = false,
      value,
      onChange,
      autoFocus,
      icons = [],
      label = 'Password',
      inputClassName
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [capsLockWarning, setCapsLockWarning] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
      const keyHandler = (e: KeyboardEvent) => {
        if (e.getModifierState) {
          setCapsLockWarning(e.getModifierState('CapsLock'));
        }
      };
      window.addEventListener('keydown', keyHandler);
      window.addEventListener('keyup', keyHandler);
      return () => {
        window.removeEventListener('keydown', keyHandler);
        window.removeEventListener('keyup', keyHandler);
      };
    }, []);

    return (
      <Input
        label={label}
        type={showPassword ? 'text' : 'password'}
        warning={warning}
        value={value}
        onChange={onChange}
        autofocus={autoFocus}
        ref={ref}
        inputClassName={inputClassName}
        icons={[
          <button
            type="button"
            className="text-textSecondary"
            onClick={() => setShowPassword(!showPassword)}
            key={'password-icon'}
          >
            {showPassword ? <LockOpen size={20} /> : <Lock size={20} />}
          </button>,
          ...(capsLockWarning
            ? [
                <span key={'caps-icon'} className="flex items-center gap-1">
                  <ArrowUp size={20} />
                </span>
              ]
            : []),
          ...icons
        ]}
      />
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
